import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, Stack, Alert,
  FormControlLabel, Switch, MenuItem, Select, FormControl, InputLabel, Divider,
} from '@mui/material';
import {
  Apartment as ApartmentIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import {
  useEmployers, useUpsertEmployer, useFinancialYears, useCreateFinancialYear,
} from '../../../hooks/useForm16';
import { SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';

// ---- FORTHEYE soft-card tokens ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const FIELD_SX = { '& .MuiOutlinedInput-root': { borderRadius: '12px' } };
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

const EMPTY = {
  name: '', address_line1: '', address_line2: '', city: '', state: '',
  pincode: '', pan: '', tan: '', cit_tds_address: '',
  signatory_name: '', signatory_designation: '', signatory_place: '',
  signatory_signature: '',
  is_active: true,
};

const MAX_SIGNATURE_BYTES = 2 * 1024 * 1024; // 2 MB

export default function EmployerSetup() {
  const { data: employers = [] } = useEmployers();
  const upsert = useUpsertEmployer();
  const { data: fys = [] } = useFinancialYears();
  const createFy = useCreateFinancialYear();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [fyForm, setFyForm] = useState({ label: '', start_date: '', end_date: '', assessment_year: '' });

  useEffect(() => {
    if (employers.length && editingId === null) {
      const active = employers.find((e) => e.is_active) || employers[0];
      setEditingId(active.id);
      setForm({ ...active });
    }
  }, [employers, editingId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const onSignatureFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please choose an image file.'); return; }
    if (file.size > MAX_SIGNATURE_BYTES) { alert('Signature image is too large (max 2 MB).'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, signatory_signature: reader.result }));
    reader.readAsDataURL(file); // stores a base64 data URI string
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ApartmentIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Form 16 — Employer & FY Setup
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Configure the deductor (employer) details and financial years used on Form 16.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={2.5}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#111827' }}>Employer (deductor) details</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>Company identity, TAN/PAN and the authorised signatory printed on Form 16.</Typography>
          </Box>
          {employers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 240, ...FIELD_SX }}>
              <InputLabel>Edit existing</InputLabel>
              <Select label="Edit existing" MenuProps={SELECTED_MENU_PROPS} value={editingId || ''} onChange={(e) => {
                const id = e.target.value;
                setEditingId(id);
                const found = employers.find((x) => x.id === id);
                if (found) setForm({ ...found });
              }}>
                {employers.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
                ))}
                <MenuItem value="new" onClick={() => { setEditingId(null); setForm(EMPTY); }}>+ New employer</MenuItem>
              </Select>
            </FormControl>
          )}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><TextField fullWidth label="Name" value={form.name} onChange={set('name')} sx={FIELD_SX} /></Grid>
          <Grid item xs={6} md={3}><TextField fullWidth label="PAN" value={form.pan} onChange={set('pan')} inputProps={{ maxLength: 10 }} sx={FIELD_SX} /></Grid>
          <Grid item xs={6} md={3}><TextField fullWidth label="TAN" value={form.tan} onChange={set('tan')} inputProps={{ maxLength: 10 }} sx={FIELD_SX} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Address line 1" value={form.address_line1} onChange={set('address_line1')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Address line 2" value={form.address_line2 || ''} onChange={set('address_line2')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="City" value={form.city} onChange={set('city')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="State" value={form.state} onChange={set('state')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="Pincode" value={form.pincode} onChange={set('pincode')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={2} label="CIT(TDS) address (printed on Form 16)" value={form.cit_tds_address || ''} onChange={set('cit_tds_address')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="Signatory name" value={form.signatory_name || ''} onChange={set('signatory_name')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="Signatory designation" value={form.signatory_designation || ''} onChange={set('signatory_designation')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="Place" value={form.signatory_place || ''} onChange={set('signatory_place')} sx={FIELD_SX} /></Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Button variant="outlined" component="label" size="small" startIcon={<AttachFileIcon />} sx={OUTLINED_BTN}>
                {form.signatory_signature ? 'Change signature image' : 'Upload signature image'}
                <input hidden type="file" accept="image/*" onChange={onSignatureFile} />
              </Button>
              {form.signatory_signature && (
                <>
                  <Box component="img" src={form.signatory_signature} alt="Signatory signature"
                    sx={{ height: 48, maxWidth: 200, objectFit: 'contain', border: '1px solid #eef0f4', borderRadius: '12px', p: 0.5, bgcolor: '#fff' }} />
                  <Button size="small" color="error" onClick={() => setForm({ ...form, signatory_signature: '' })} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px' }}>Remove</Button>
                </>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Visible signature printed on Form 16 above the signatory name (PNG/JPG, max 2 MB). This is not a digital/DSC signature.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={!!form.is_active} onChange={set('is_active')} />} label="Active" />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1.5} mt={2} alignItems="center" flexWrap="wrap">
          <Button variant="contained" disableElevation disabled={upsert.isPending} sx={GRADIENT_BTN}
            onClick={() => upsert.mutate({ id: editingId === 'new' ? null : editingId, ...form }, {
              onSuccess: (saved) => { setEditingId(saved.id); setForm(saved); },
            })}
          >Save</Button>
          {upsert.isError && <Alert severity="error" sx={{ borderRadius: '14px' }}>Failed to save employer.</Alert>}
          {upsert.isSuccess && <Alert severity="success" sx={{ borderRadius: '14px' }}>Saved.</Alert>}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={CARD_SX}>
        <Box mb={2.5}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#111827' }}>Financial Years</Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>Define the financial years and their assessment years available for Form 16.</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Label (e.g. 2024-25)" value={fyForm.label} onChange={(e) => setFyForm({ ...fyForm, label: e.target.value })} sx={FIELD_SX} /></Grid>
          <Grid item xs={6} md={3}><TextField fullWidth size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={fyForm.start_date} onChange={(e) => setFyForm({ ...fyForm, start_date: e.target.value })} sx={FIELD_SX} /></Grid>
          <Grid item xs={6} md={3}><TextField fullWidth size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={fyForm.end_date} onChange={(e) => setFyForm({ ...fyForm, end_date: e.target.value })} sx={FIELD_SX} /></Grid>
          <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Assessment Year (e.g. 2025-26)" value={fyForm.assessment_year} onChange={(e) => setFyForm({ ...fyForm, assessment_year: e.target.value })} sx={FIELD_SX} /></Grid>
          <Grid item xs={12}>
            <Button variant="contained" disableElevation sx={GRADIENT_BTN} onClick={() => createFy.mutate(fyForm, {
              onSuccess: () => setFyForm({ label: '', start_date: '', end_date: '', assessment_year: '' }),
            })}>Add FY</Button>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        {fys.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No FY configured yet.</Typography>
        ) : (
          <Grid container spacing={1.5}>
            {fys.map((fy) => (
              <Grid key={fy.id} item xs={12} sm={6} md={4}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', border: '1px solid #eef0f4', bgcolor: '#fcfdff' }}>
                  <Typography sx={{ fontWeight: 700, color: '#111827' }}>{fy.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>AY {fy.assessment_year}</Typography>
                  <Typography variant="caption" display="block" sx={{ color: '#6b7280' }}>{fy.start_date} → {fy.end_date}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
}
